import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

type AssetPerformance = {
  asset: string;
  totalGain: number;
  totalProceeds: number;
  totalCost: number;
  totalQuantity: number;
  disposalCount: number;
  gainPercent?: string;
  lossPercent?: string;
};

type TopPerformersSummary = {
  totalGains: number;
  totalLosses: number;
  netGainLoss: number;
  gainerCount: number;
  loserCount: number;
};

export type TaxTopPerformers = {
  taxYear: string;
  gainers: AssetPerformance[];
  losers: AssetPerformance[];
  summary: TopPerformersSummary;
};

export const useTaxTopPerformers = ({
  taxYear,
  limit = 5,
  enabled = true,
}: {
  taxYear: string | number;
  limit?: number;
  enabled?: boolean;
}): {
  isLoading: boolean;
  error: Error | null;
  data: TaxTopPerformers | undefined;
} => {
  const { getToken, user } = useAuth();

  const getTopPerformers = async (): Promise<TaxTopPerformers> => {
    const url = `/tax/top-movers/${taxYear}?limit=${limit}`;

    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(url);
      return res.data;
    } catch (error) {
      console.error("[useTaxTopPerformers] error", error);
      throw error;
    }
  };

  const {
    isLoading,
    error,
    data,
  }: UseQueryResult<TaxTopPerformers, Error> = useQuery({
    queryKey: ["tax_top_performers", taxYear, limit],
    queryFn: getTopPerformers,
    enabled: !!user?.email && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return { isLoading, error, data };
};
